import { Story } from "../types";
import { HeartIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import { cn } from "../utils/cn";

interface StoryCardProps {
  story: Story;
  onLike?: (id: string) => void;
  liked?: boolean;
  onEdit?: (story: Story) => void;
  currentUserId?: string;
}

// Helper for robust date formatting (Firestore Timestamp, string, Date)
function formatDate(val: any): string {
  if (!val) return "";
  if (typeof val === "string" || typeof val === "number") {
    // string (possibly ISO) or timestamp as ms
    const d = new Date(val);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  }
  if (val.toDate && typeof val.toDate === "function") {
    // Firestore Timestamp
    const d = val.toDate();
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  }
  if (val instanceof Date) {
    return val.toLocaleDateString();
  }
  return "";
}

export default function StoryCard({
  story,
  onLike,
  liked = false,
  onEdit,
  currentUserId,
}: StoryCardProps) {
  const categoryColors = {
    success: "bg-emerald-100 text-emerald-800 border-emerald-200",
    impact: "bg-cyan-100 text-cyan-800 border-cyan-200",
    community: "bg-purple-100 text-purple-800 border-purple-200",
  };

  const isAuthor =
    currentUserId &&
    (story.author_id === currentUserId || story.author?.id === currentUserId);
  return (
    <div className="transition-all duration-700 card-floating group">
      {/* Image */}
      <div className="relative mb-4 overflow-hidden rounded-xl">
        <img
          src={
            story.images?.[0] ||
            "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800"
          }
          alt={story.title}
          className="object-cover w-full h-48 transition-all duration-500 group-hover:scale-105 group-hover:rotate-1"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span
            className={cn(
              "inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg shadow-lg backdrop-blur-sm border-2 capitalize",
              categoryColors[story.category]
            )}
          >
            {story.category === "success" && "🎉"}
            {story.category === "impact" && "💚"}
            {story.category === "community" && "🤝"}
            {story.category}
          </span>
        </div>

        {/* Edit Button */}
        {isAuthor && onEdit && (
          <button
            onClick={() => onEdit(story)}
            className="absolute p-2 bg-white/95 backdrop-blur-xl rounded-lg shadow-lg top-3 right-3 hover:bg-white hover:shadow-xl transition-all duration-500 transform hover:scale-105 hover:rotate-2"
            title="Edit Story"
          >
            <PencilIcon className="w-4 h-4 text-slate-600" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold leading-tight text-slate-900 group-hover:text-emerald-600 transition-colors duration-500">
          {story.title}
        </h3>

        <p className="text-slate-600 line-clamp-3 leading-relaxed text-sm">
          {story.content}
        </p>

        {/* Author & Date */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="relative">
              {story.author_photo_url || story.author?.photoURL ? (
                <img
                  src={story.author_photo_url || story.author?.photoURL}
                  alt={story.author_name || story.author?.name}
                  className="object-cover w-12 h-12 rounded-lg ring-2 ring-emerald-200 shadow-md"
                />
              ) : (
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-200 ring-2 ring-emerald-200 shadow-md">
                  <span className="text-lg font-bold text-emerald-700">
                    {(story.author_name || story.author?.name || "A")
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
              )}
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow-md"></div>
            </div>
            <div>
              <p className="font-bold text-slate-900 text-base">
                {story.author_name || story.author?.name || "Anonymous"}
              </p>
              <div className="flex items-center text-xs text-slate-500">
                <CalendarIcon className="w-3 h-3 mr-1" />
                <span className="font-semibold">
                  {formatDate(story.created_at || story.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Like Button */}
          <button
            onClick={() => onLike?.(story.id)}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-red-50 rounded-lg transition-all duration-500 group transform hover:scale-105 hover:-translate-y-0.5 shadow-md hover:shadow-lg"
          >
            {liked ? (
              <HeartSolidIcon className="w-4 h-4 text-red-500 group-hover:scale-110 transition-transform duration-500" />
            ) : (
              <HeartIcon className="w-4 h-4 text-slate-500 group-hover:text-red-500 transition-colors duration-500" />
            )}
            <span className="font-bold text-slate-700 group-hover:text-red-600 transition-colors duration-500 text-sm">
              {story.likes}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
